import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const RecommendationAlgorithms: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Industry Insights • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          How Video Recommendation Algorithms Work
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Your homepage shows exactly the videos you want to watch—but you never asked for them. Behind this personalized feed is a recommendation algorithm analyzing billions of data points to predict what keeps you watching. Understanding how these algorithms work helps creators optimize content and viewers regain control over what they consume.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Goal of Recommendation Algorithms
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Platforms want you to watch more videos, longer. The algorithm's job: predict which videos keep you engaged and show those first. It's not random—it's optimization for watch time.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Platform wins:</strong> More watch time = more ad revenue</li>
          <li><strong>Creators win:</strong> Good content gets discovered, even from small channels</li>
          <li><strong>Viewers win (ideally):</strong> Discover content they enjoy without manual searching</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Data the Algorithm Collects
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          To recommend accurately, the algorithm tracks everything you do:
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Your Viewing Behavior
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watch history:</strong> Videos you've watched, when, for how long</li>
          <li><strong>Search history:</strong> What you search for reveals intent</li>
          <li><strong>Likes/dislikes:</strong> Direct feedback signals</li>
          <li><strong>Subscriptions:</strong> Channels you follow indicate interests</li>
          <li><strong>Watch time per video:</strong> Did you watch 10% or 100%? High retention = algorithm boost</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Video Metadata
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Title and description:</strong> Keywords indicate topic</li>
          <li><strong>Tags:</strong> Creator-defined categories</li>
          <li><strong>Thumbnail:</strong> Visual analysis (AI scans image content)</li>
          <li><strong>Upload date:</strong> Fresh content gets temporary boost</li>
          <li><strong>Category:</strong> Education, Entertainment, News, etc.</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Engagement Metrics
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Click-through rate (CTR):</strong> % of people who click when shown the video</li>
          <li><strong>Average view duration (AVD):</strong> How much of the video viewers watch on average</li>
          <li><strong>Likes, comments, shares:</strong> Engagement signals quality</li>
          <li><strong>Re-watch rate:</strong> Do people watch multiple times?</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How the Algorithm Decides What to Recommend
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 1: Candidate Generation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          From millions of videos, the algorithm narrows down to hundreds of candidates. It uses:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Collaborative filtering:</strong> "Users like you also watched X"</li>
          <li><strong>Content-based filtering:</strong> "You watched cooking videos, here are more cooking videos"</li>
          <li><strong>Trending content:</strong> Videos getting unusual spikes in views</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 2: Ranking
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Of those hundreds, rank by predicted watch time. The algorithm asks: "How likely is this user to watch this video for X minutes?"
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Machine learning model trained on billions of past viewing sessions</li>
          <li>Factors: your history, video performance, freshness, creator track record</li>
          <li>Output: ranked list from most-to-least likely to engage you</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Step 3: Personalization
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Your homepage isn't static—it refreshes based on context. Morning vs night, mobile vs desktop, WiFi vs mobile data—algorithm adjusts recommendations dynamically.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Key Metrics That Influence Recommendations
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Watch Time (Most Important)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Total minutes watched matters more than views. A 10-minute video watched fully (10 min watch time) beats a 3-minute video watched fully (3 min watch time).
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Click-Through Rate (CTR)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If your thumbnail/title gets clicks when shown, the algorithm shows it to more people. Low CTR = fewer impressions.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Average View Duration (AVD)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          What % of your video do viewers watch? 70%+ retention signals high quality. Under 30% = algorithm buries it.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Session Time
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Does your video lead viewers to watch MORE videos after? If yes, algorithm favors it. If viewers leave the platform after watching, algorithm penalizes it.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Algorithm Myths (Debunked)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Myth 1: "Big channels always get recommended"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>False.</strong> Small channels with high engagement can outperform big channels with low engagement. The algorithm cares about video performance, not subscriber count.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Myth 2: "Posting daily boosts algorithm"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Partially true.</strong> More videos = more chances for discovery, BUT if quality suffers, watch time drops and algorithm penalizes you. Quality {'>'} quantity.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Myth 3: "Asking for likes/comments helps"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Partially true.</strong> Engagement signals quality, so asking can help slightly. But forced engagement ("Like or I'll be sad") annoys viewers and hurts watch time—net negative.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Myth 4: "The algorithm is against me"
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>False.</strong> The algorithm has no agenda. It promotes what keeps people watching. If your content isn't recommended, viewers aren't watching long enough—improve retention, not blame the algorithm.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How IndeeClips Algorithm Differs from YouTube
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Regional Content Priority
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips boosts regional language content. If you watch Hindi videos, you'll see more Hindi creators—not random English viral trends. YouTube defaults to global viral content.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Small Creator Boost
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          New videos from channels under 10K subs get temporary exposure boost. This levels the playing field—small creators can compete with established ones.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Less Clickbait Tolerance
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          High CTR + low watch time = clickbait. IndeeClips algorithm penalizes this heavily. YouTube's algorithm is more forgiving of clickbait if total watch time is decent.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How Creators Can "Game" the Algorithm (Ethically)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Optimize for Watch Time
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Hook viewers early:</strong> First 15 seconds must grab attention or viewers leave</li>
          <li><strong>Cut fluff:</strong> Every second should add value—remove intros, filler, tangents</li>
          <li><strong>Pattern interrupts:</strong> Change visuals, music, or pace every 30-60 seconds to maintain attention</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Improve CTR
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Test thumbnails:</strong> A/B test different designs—faces, bright colors, text overlays perform best</li>
          <li><strong>Curiosity-driven titles:</strong> "How I grew 10K subs in 30 days" {'>'} "My subscriber growth story"</li>
          <li><strong>Avoid misleading:</strong> If thumbnail promises X but video delivers Y, viewers leave early (hurts AVD)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Create Binge-Worthy Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          End videos with a teaser for the next one. If viewers click through to another video, session time increases—algorithm rewards this.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Dark Side: Filter Bubbles and Echo Chambers
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Algorithms optimize for engagement, not truth or diversity. This creates problems:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Filter bubbles:</strong> You only see content confirming your existing beliefs</li>
          <li><strong>Radicalization:</strong> Extreme content keeps people watching longer, so algorithm promotes it</li>
          <li><strong>Misinformation:</strong> Sensational false claims outperform boring truth</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Platforms combat this with fact-checking, diverse recommendations, and limiting extreme content—but it's an ongoing challenge.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Regain Control as a Viewer
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Use "Not Interested":</strong> Train the algorithm to show less of certain content</li>
          <li><strong>Clear watch history:</strong> Resets recommendations—useful if stuck in a bubble</li>
          <li><strong>Subscribe intentionally:</strong> Follow channels you genuinely value, not random viral creators</li>
          <li><strong>Search manually:</strong> Don't rely only on homepage—actively search for diverse topics</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            The Algorithm Is a Tool, Not a Mystery
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Recommendation algorithms aren't magic—they're optimization engines trained on data. Creators who understand watch time, CTR, and engagement can optimize content for discovery. Viewers who understand filter bubbles can regain control over what they consume. The algorithm serves whoever understands it best.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about how IndeeClips recommendations work? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default RecommendationAlgorithms;
